import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    page: Object,
    currency: String,
    userId: String,
    cartId: Number
  }

  connect() {
    window.dataLayer = window.dataLayer || []
    this.lastPath = null

    this.onTurboLoad = this.handleTurboLoad.bind(this)
    this.onAddToCart = this.handleCustomEvent.bind(this, "add_to_cart")
    this.onBeginCheckout = this.handleCustomEvent.bind(this, "begin_checkout")
    this.onPurchase = this.handleCustomEvent.bind(this, "purchase")

    document.addEventListener("turbo:load", this.onTurboLoad)
    document.addEventListener("oe2:analytics:add_to_cart", this.onAddToCart)
    document.addEventListener("oe2:analytics:begin_checkout", this.onBeginCheckout)
    document.addEventListener("oe2:analytics:purchase", this.onPurchase)

    // Fire on initial load as soon as controller connects
    this.handleTurboLoad()
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.onTurboLoad)
    document.removeEventListener("oe2:analytics:add_to_cart", this.onAddToCart)
    document.removeEventListener("oe2:analytics:begin_checkout", this.onBeginCheckout)
    document.removeEventListener("oe2:analytics:purchase", this.onPurchase)
  }

  handleTurboLoad() {
    const path = `${window.location.pathname}${window.location.search}`
    if (path === this.lastPath) return

    this.lastPath = path
    this.pushEvent("page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title
    })

    this.firePageScopedEvents()
  }

  firePageScopedEvents() {
    const ctx = this.hasPageValue ? this.pageValue : {}
    if (!ctx || !ctx.type) return

    switch (ctx.type) {
      case "list":
        this.pushEvent("view_item_list", {
          item_list_id: ctx.list_id,
          item_list_name: ctx.list_name,
          items: ctx.items || [],
          filters: ctx.filters,
          results_count: ctx.results_count
        })
        break
      case "product":
        this.pushEvent("view_item", {
          currency: ctx.currency || this.currentCurrency,
          value: ctx.value,
          items: ctx.item ? [ctx.item] : []
        })
        break
      case "checkout":
        this.pushEvent("begin_checkout", {
          currency: ctx.currency || this.currentCurrency,
          value: ctx.value,
          items: ctx.items || [],
          cart_id: ctx.cart_id || this.cartId
        })
        break
      case "purchase":
        this.pushEvent("purchase", {
          currency: ctx.currency || this.currentCurrency,
          transaction_id: ctx.transaction_id,
          value: ctx.value,
          tax: ctx.tax,
          shipping: ctx.shipping,
          coupon: ctx.coupon,
          items: ctx.items || []
        })
        break
      default:
        break
    }
  }

  selectItem(event) {
    const target = event.currentTarget
    if (!target) return

    const itemData = this.parseJSON(target.dataset.analyticsItem)
    if (!itemData) return

    const payload = {
      item_list_id: target.dataset.analyticsListId || this.pageListId,
      item_list_name: target.dataset.analyticsListName || this.pageListName,
      items: [
        {
          ...itemData,
          index: this.parseNumber(target.dataset.analyticsIndex) || itemData.index
        }
      ]
    }

    this.pushEvent("select_item", payload)
  }

  handleCustomEvent(eventName, domEvent) {
    const detail = domEvent?.detail || {}
    if (!detail || Object.keys(detail).length === 0) return

    const payload = { ...detail }
    if (!payload.currency) payload.currency = this.currentCurrency
    if (!payload.items) payload.items = []
    if (eventName === "begin_checkout" && !payload.cart_id && this.cartId) {
      payload.cart_id = this.cartId
    }

    this.pushEvent(eventName, payload)
  }

  pushEvent(name, params = {}) {
    const payload = { ...params }
    if (this.hasUserIdValue && this.userIdValue) {
      payload.user_id = this.userIdValue
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: name, ...payload })

    if (typeof window.gtag === "function") {
      window.gtag("event", name, payload)
    }
  }

  parseJSON(value) {
    if (!value) return null
    try {
      return JSON.parse(value)
    } catch (error) {
      console.warn("Analytics: unable to parse JSON", value)
      return null
    }
  }

  parseNumber(value) {
    if (value === undefined) return null
    const number = Number(value)
    return Number.isNaN(number) ? null : number
  }

  get currentCurrency() {
    return this.hasCurrencyValue && this.currencyValue ? this.currencyValue : "GBP"
  }

  get cartId() {
    return this.hasCartIdValue ? this.cartIdValue : null
  }

  get pageListId() {
    return this.hasPageValue ? this.pageValue.list_id : undefined
  }

  get pageListName() {
    return this.hasPageValue ? this.pageValue.list_name : undefined
  }
}
